import { useEffect, useRef, useState } from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";

/**
 * Multi-select picker backed by /actions/scheduling/coaches.
 * Props:
 *   id              — element id for the trigger button (used by <label htmlFor>)
 *   value           — string[] of selected coach _ids
 *   onChange        — (ids: string[]) => void
 *   placeholder     — string shown when nothing is selected (default "Select coaches")
 *   disabled        — boolean
 */
const CoachMultiSelect = ({
  id,
  value,
  onChange,
  placeholder = "Select coaches",
  disabled = false,
}) => {
  const { locals } = useRillContext();
  const theme = locals?.theme;
  const selected = Array.isArray(value) ? value.map(String) : [];
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch("/actions/scheduling/coaches", { credentials: "same-origin" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return Array.isArray(data?.coaches) ? data.coaches : [];
      })
      .then((list) => {
        if (cancelled) return;
        setCoaches(list);
        if (list.length === 1) onChange?.([String(list[0]._id)]);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Failed to load coaches");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, [open]);

  const toggleId = (cid) => {
    const set = new Set(selected);
    if (set.has(cid)) set.delete(cid);
    else set.add(cid);
    onChange?.(Array.from(set));
  };

  const clearAll = (e) => {
    e?.stopPropagation?.();
    onChange?.([]);
  };

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return coaches;
    return coaches.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
    );
  })();

  const selectedCoaches = coaches.filter((c) => selected.includes(c._id));
  const triggerLabel =
    selectedCoaches.length === 0
      ? placeholder
      : selectedCoaches.length === 1
        ? selectedCoaches[0].name
        : `${selectedCoaches.length} coaches selected`;

  const avatarFor = (coach) =>
    coach?.profile_image_url || getAvatarUrl(coach || {}, false, theme);

  if (!loading && !error && coaches.length === 1) {
    const sole = coaches[0];
    return (
      <div className="coach-multi-select coach-multi-select--readonly">
        <div className="coach-multi-select-trigger-wrap">
          <div className="coach-multi-select-trigger">
            <img
              src={avatarFor(sole)}
              alt=""
              className="coach-multi-select-avatar"
            />
            <span className="coach-multi-select-trigger-label">{sole.name}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coach-multi-select" ref={wrapRef}>
      <div className="coach-multi-select-trigger-wrap">
        <button
          type="button"
          id={id}
          className="coach-multi-select-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          {selectedCoaches.length === 1 && (
            <img
              src={avatarFor(selectedCoaches[0])}
              alt=""
              className="coach-multi-select-avatar"
            />
          )}
          {selectedCoaches.length > 1 && (
            <span className="coach-multi-select-avatar-stack" aria-hidden>
              {selectedCoaches.slice(0, 3).map((c) => (
                <img
                  key={c._id}
                  src={avatarFor(c)}
                  alt=""
                  className="coach-multi-select-avatar coach-multi-select-avatar--stacked"
                />
              ))}
            </span>
          )}
          <span
            className={
              selectedCoaches.length === 0
                ? "coach-multi-select-trigger-label is-placeholder"
                : "coach-multi-select-trigger-label"
            }
          >
            {triggerLabel}
          </span>
          <img
            src="/img/new-icons/chevron-down.svg"
            alt=""
            className="coach-multi-select-chevron"
            aria-hidden
          />
        </button>
        {selectedCoaches.length > 0 && (
          <button
            type="button"
            className="coach-multi-select-clear"
            aria-label="Clear selection"
            onClick={clearAll}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <div className="coach-multi-select-panel" role="listbox">
          <div className="coach-multi-select-search">
            <input
              type="text"
              placeholder="Search coaches…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search coaches"
            />
          </div>
          {loading ? (
            <div className="coach-multi-select-empty">Loading coaches…</div>
          ) : error ? (
            <div className="coach-multi-select-empty" role="alert">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="coach-multi-select-empty">
              {coaches.length === 0
                ? "No coaches found."
                : "No coaches match your search."}
            </div>
          ) : (
            <ul className="coach-multi-select-list">
              {filtered.map((c) => {
                const isSelected = selected.includes(c._id);
                return (
                  <li key={c._id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={
                        isSelected
                          ? "coach-multi-select-option is-selected"
                          : "coach-multi-select-option"
                      }
                      onClick={() => toggleId(c._id)}
                    >
                      <span
                        aria-hidden
                        className={
                          isSelected
                            ? "coach-multi-select-check is-checked"
                            : "coach-multi-select-check"
                        }
                      >
                        {isSelected && (
                          <img
                            src="/img/new-icons/checked-square.svg"
                            alt=""
                          />
                        )}
                        {!isSelected && (
                          <img
                            src="/img/new-icons/unchecked-square.svg"
                            alt=""
                          />
                        )}
                      </span>
                      <img
                        src={avatarFor(c)}
                        alt=""
                        className="coach-multi-select-avatar"
                      />
                      <span className="coach-multi-select-option-body">
                        <span className="coach-multi-select-option-name">
                          {c.name}
                        </span>
                        {c.email && (
                          <span className="coach-multi-select-option-email">
                            {c.email}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachMultiSelect;
